/**
 * TanStack Query hooks for `api-rate-limiting`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	apiRateLimitingQueryKeys,
	clearOverrides,
	getLimits,
	getUsage,
	saveOverrides,
	type LimitsResponse,
	type SaveOverridesBody,
	type SaveOverridesResponse,
	type UsageResponse,
} from "./api";

export function useRateLimits(
	apiKeyId: string,
): UseQueryResult<LimitsResponse, Error> {
	return useQuery({
		queryKey: apiRateLimitingQueryKeys.limits(apiKeyId),
		queryFn: () => getLimits(apiKeyId),
		enabled: apiKeyId.length > 0,
	});
}

export function useRateUsage(
	apiKeyId: string,
): UseQueryResult<UsageResponse, Error> {
	return useQuery({
		queryKey: apiRateLimitingQueryKeys.usage(apiKeyId),
		queryFn: () => getUsage(apiKeyId),
		enabled: apiKeyId.length > 0,
		refetchInterval: 15_000,
	});
}

export function useSaveOverrides(): UseMutationResult<
	SaveOverridesResponse,
	Error,
	SaveOverridesBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: saveOverrides,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: apiRateLimitingQueryKeys.all });
		},
	});
}

export function useClearOverrides(): UseMutationResult<
	LimitsResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: clearOverrides,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: apiRateLimitingQueryKeys.all });
		},
	});
}
